import type { Comic } from '@tanko/shared'
import { CloudApiClient, type CollectionMode, type CollectionSummary } from './api-client'
import { mirrorCollections } from '../db/repo'
import { isRegistered } from './comic-sync'

/**
 * 集合的唯一寫入入口。
 *
 * 與分類相同的取捨：真相是雲端目錄，每個變更先呼叫 API，成功之後才重抓集合清單並更新
 * 鏡像；失敗時鏡像原封不動。離線或登入過期時不能改集合，換到的是不需要離線變更佇列。
 *
 * 呼叫端（cloud-library store）在成功後重新整理整份清單——成員異動會改變書架的折疊結果。
 */

const api = new CloudApiClient()
type CollectionClient = Pick<CloudApiClient,
  'collections' | 'createCollection' | 'updateCollection' | 'addCollectionMembers' | 'removeCollectionMember'
  | 'reorderCollection' | 'dissolveCollection' | 'deleteCollectionWithMembers'>
export type CollectionReader = Pick<CloudApiClient, 'collections'>

/** 一次加入的結果：送出去的雲端識別碼，以及被擋下的未註冊作品標題。 */
export type MembershipInput = { comicIds: string[]; skipped: string[] }

/**
 * 把選取拆成「能送出的雲端識別碼」與「還沒註冊、只能留在書架上的」。
 * 集合的真相在雲端，未註冊的作品在那裡沒有對應——與分類指派同一條規則。
 */
export function splitRegistered(comics: readonly Comic[]): MembershipInput {
  const comicIds: string[] = []
  const skipped: string[] = []
  for (const comic of comics) {
    if (isRegistered(comic) && comic.cloudComicId) comicIds.push(comic.cloudComicId)
    else skipped.push(comic.title)
  }
  return { comicIds: [...new Set(comicIds)], skipped }
}

/** 未註冊作品的說明。四本已註冊加一本未註冊時，四本照常送出，這句話說明剩下那本。 */
export function skippedMessage(skipped: readonly string[]): string {
  if (!skipped.length) return ''
  const names = skipped.map(title => `「${title}」`).join('、')
  return `${names}還沒有加入雲端目錄，請等註冊完成後再加入集合。`
}

/** 取得雲端集合並同步鏡像。 */
export async function loadCollections(client: CollectionReader = api): Promise<CollectionSummary[]> {
  const { items } = await client.collections()
  await mirrorCollections(items)
  return items
}

export async function createCollection(title: string, comics: readonly Comic[], client: CollectionClient = api, mode: CollectionMode = 'series'): Promise<{ collection: CollectionSummary; message: string }> {
  const trimmed = title.trim()
  if (!trimmed) throw new Error('請填寫集合標題。')
  const { comicIds, skipped } = splitRegistered(comics)
  if (!comicIds.length) throw new Error(skippedMessage(skipped) || '請至少選一本作品。')
  const collection = await client.createCollection(trimmed, comicIds, mode)
  await loadCollections(client)
  return { collection, message: skippedMessage(skipped) }
}

export async function addToCollection(collectionId: string, comics: readonly Comic[], client: CollectionClient = api): Promise<{ collection: CollectionSummary; message: string }> {
  const { comicIds, skipped } = splitRegistered(comics)
  if (!comicIds.length) throw new Error(skippedMessage(skipped) || '請至少選一本作品。')
  const collection = await client.addCollectionMembers(collectionId, comicIds)
  await loadCollections(client)
  return { collection, message: skippedMessage(skipped) }
}

export async function updateCollection(
  collectionId: string, patch: { title?: string; coverComicId?: string | null; categoryIds?: readonly string[]; mode?: CollectionMode }, client: CollectionClient = api,
): Promise<CollectionSummary> {
  const title = patch.title === undefined ? undefined : patch.title.trim()
  if (title !== undefined && !title) throw new Error('請填寫集合標題。')
  // 切換模式只送 mode：不碰任何成員的閱讀狀態，那些本來就在成員身上。
  const collection = await client.updateCollection(collectionId, {
    ...(title === undefined ? {} : { title }),
    ...(patch.coverComicId === undefined ? {} : { coverComicId: patch.coverComicId }),
    ...(patch.categoryIds === undefined ? {} : { categoryIds: [...new Set(patch.categoryIds)] }),
    ...(patch.mode === undefined ? {} : { mode: patch.mode }),
  })
  await loadCollections(client)
  return collection
}

export async function removeFromCollection(collectionId: string, comicId: string, client: CollectionClient = api): Promise<{ dissolved: boolean }> {
  const result = await client.removeCollectionMember(collectionId, comicId)
  await loadCollections(client)
  return result
}

export async function reorderCollection(collectionId: string, comicIds: readonly string[], client: CollectionClient = api): Promise<CollectionSummary> {
  const collection = await client.reorderCollection(collectionId, comicIds)
  await loadCollections(client)
  return collection
}

export async function dissolveCollection(collectionId: string, client: CollectionClient = api): Promise<void> {
  await client.dissolveCollection(collectionId)
  await loadCollections(client)
}

export async function deleteCollectionWithMembers(collectionId: string, client: CollectionClient = api): Promise<void> {
  await client.deleteCollectionWithMembers(collectionId)
  await loadCollections(client)
}
